/**
 * The four-column PROJECTION of a price-limit row (CU-869fkbjdh, UX
 * rethink § 3.1): Where · When · Floor · Ceiling. A row of
 * `repricing_price_limit` is two choices — when the dates are read
 * (`protects_dates`) and where the floor comes from (`matrix_id`) — and
 * these helpers turn them into words, so the table, the drawer's
 * "Reads as:" line, the delete confirmation and the CSV preview all say
 * the same thing.
 *
 * Pure: no React, no fetch, no specificity and no matching — the engine
 * ranks and resolves (`POST /setup/price-limits/resolve`); the screen
 * only projects. `node --test`.
 */
import { fromIso, toIso } from '../../@services/dateRange.js'

export const MAX_PROTECTED_DAYS = 30

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const pad2 = (n) => String(n).padStart(2, '0')

/** `2026-12-26` → `26 Dec 2026`; anything else → `—`. */
export function fmtDate(iso) {
	const d = fromIso(iso)
	return d ? `${pad2(d.getDate())} ${MONTHS[d.getMonth()]} ${d.getFullYear()}` : '—'
}

/** `26 Dec` when the year is the same as `other`'s, else the full date. */
export function fmtDateShort(iso, other) {
	const d = fromIso(iso)
	if (!d) return '—'
	const o = fromIso(other)
	return o && o.getFullYear() === d.getFullYear()
		? `${pad2(d.getDate())} ${MONTHS[d.getMonth()]}`
		: fmtDate(iso)
}

/** `iso + n days` as ISO, or null. */
export function addDays(iso, n) {
	const d = fromIso(iso)
	if (!d) return null
	d.setDate(d.getDate() + Number(n))
	return toIso(d)
}

export const fmtMoney = (n) => (n == null || n === '' || Number.isNaN(Number(n)) ? '—' : Number(n).toFixed(2))

// ---------------------------------------------------------------------------
// Classification
// ---------------------------------------------------------------------------

/** 'protection' | 'matrix' | 'floor' — the two choices of the model, read as the operator does. */
export function kindOf(r) {
	if (r.protects_dates) return 'protection'
	if (isBinding(r)) return 'matrix'
	return 'floor'
}

/** `''` is a binding still choosing its matrix; `null` is an ordinary rule. */
export const isBinding = (r) => r.matrix_id != null && r.matrix_id !== ''

// ---------------------------------------------------------------------------
// Where
// ---------------------------------------------------------------------------

export const WILDCARD_WHERE = 'Everywhere · every ACRISS · any length'

/** `{ min, max }` → `1–3 days` · `8+ days` · `up to 7 days`. */
export function durationLabel(d) {
	if (!d) return null
	if (d.min != null && d.max != null) {
		return d.min === d.max ? `${d.min} day${d.min === 1 ? '' : 's'}` : `${d.min}–${d.max} days`
	}
	if (d.min != null) return `${d.min}+ days`
	if (d.max != null) return `up to ${d.max} days`
	return null
}

/**
 * The chips of the Where column, in fixed order: office/group, categories,
 * rental length, offer class (franchise · tariff type · insurance).
 * `labels = { office(code), group(code), macro(code), franchise(id),
 * insurance(id) }`, each returning a string. A binding shows neither
 * categories nor length: the matrix sets them.
 */
export function whereChips(r, labels = {}) {
	const L = (fn, v) => (typeof fn === 'function' ? fn(v) : null) || String(v)
	const out = []
	if (r.scope_office_group) out.push({ kind: 'group', text: `Group · ${L(labels.group, r.scope_office_group)}` })
	for (const o of r.scope_offices || []) out.push({ kind: 'office', text: L(labels.office, o) })
	if (!isBinding(r)) {
		for (const c of r.scope_categories || []) {
			out.push(c.type === 'sipp'
				? { kind: 'sipp', text: c.code }
				: { kind: 'macro', text: `All ${L(labels.macro, c.code)}` })
		}
		const dl = durationLabel(r.scope_duration)
		if (dl) out.push({ kind: 'duration', text: dl })
	}
	if (r.scope_ps_provider_id != null) out.push({ kind: 'franchise', text: L(labels.franchise, r.scope_ps_provider_id), id: r.scope_ps_provider_id })
	if (r.scope_is_prepaid != null) out.push({ kind: 'tariff', text: r.scope_is_prepaid ? 'Prepaid' : 'Non prepaid' })
	if (r.scope_ps_insurance_id != null) out.push({ kind: 'insurance', text: L(labels.insurance, r.scope_ps_insurance_id) })
	return out
}

/** Muted tail after the chips: what is still "any" and worth saying. */
export function whereTail(r) {
	const hasPlace = !!(r.scope_office_group || r.scope_offices?.length)
	if (isBinding(r)) {
		return hasPlace ? '· categories and length from the matrix' : 'Everywhere · categories and length from the matrix'
	}
	const hasCat = !!r.scope_categories?.length
	const hasDur = !!r.scope_duration
	if (!hasPlace && !hasCat && !hasDur) return WILDCARD_WHERE
	const parts = []
	if (!hasPlace) parts.push('Everywhere')
	if (!hasCat) parts.push('every ACRISS')
	if (!hasDur) parts.push('any length')
	return parts.length ? (hasPlace ? '· ' : '') + parts.join(' · ') : null
}

// ---------------------------------------------------------------------------
// When
// ---------------------------------------------------------------------------

/** `dd Mon yyyy → dd Mon yyyy`, or the one-sided reading, or null. */
export function dateRangeLabel(r) {
	if (r.date_from && r.date_to) return `${fmtDate(r.date_from)} → ${fmtDate(r.date_to)}`
	if (r.date_from) return `from ${fmtDate(r.date_from)}`
	if (r.date_to) return `until ${fmtDate(r.date_to)}`
	return null
}

/** `{ kind: 'PICKUP' | 'PROTECTION', title, dates }` — the two lines of the When column. */
export function whenLines(r) {
	const range = dateRangeLabel(r)
	if (r.protects_dates) {
		return {
			kind: 'PROTECTION',
			title: `Protection · ${r.name || 'unnamed'}`,
			dates: range ? `Any rental up to ${MAX_PROTECTED_DAYS} days crossing ${range}` : 'Pick the dates it protects',
		}
	}
	return { kind: 'PICKUP', title: 'Pickup', dates: range || 'Any date' }
}

// ---------------------------------------------------------------------------
// Floor · Ceiling
// ---------------------------------------------------------------------------

/** Why the Ceiling cell shows `—`, or null when a ceiling is possible. */
export function ceilingNote(r) {
	if (r.protects_dates) return 'A protection is a floor; it has no ceiling'
	if (isBinding(r)) return 'A matrix sets floors only'
	return null
}

/**
 * `29–30 days unprotected` — a protection whose last band is bounded
 * (a matrix ending at 28 days, or a duration scope with a max) leaves the
 * longer rentals that cross it without a floor (CU-869fe8jrk UX 6).
 * `{ chip, text }` or null.
 */
export function unprotectedBands(r, matrix) {
	if (!r.protects_dates) return null
	let upto = null, who = null
	if (isBinding(r)) {
		const last = matrix?.base?.[matrix.base.length - 1]
		if (!matrix || !last || last.to == null) return null
		upto = Number(last.to); who = `“${matrix.name}”`
	} else if (r.scope_duration?.max != null) {
		upto = Number(r.scope_duration.max); who = `“${r.name || 'this protection'}”`
	} else return null
	if (upto >= MAX_PROTECTED_DAYS) return null
	const span = upto + 1 === MAX_PROTECTED_DAYS ? `${MAX_PROTECTED_DAYS}` : `${upto + 1}–${MAX_PROTECTED_DAYS}`
	return {
		chip: `${span} days unprotected`,
		text: `${who} prices rentals up to ${upto} days — rentals of ${span} days that cross ${r.name || 'these dates'} stay unprotected. Add an open-ended band to the matrix, or accept it.`,
	}
}

/** The "Reads as:" sentence of the drawer and of the delete confirmation. */
export function readsAs(r, labels = {}, matrix = null) {
	const parts = []
	if (r.protects_dates) {
		const range = r.date_from && r.date_to ? `${fmtDate(r.date_from)} → ${fmtDate(r.date_to)}` : '…'
		parts.push(`Protection “${r.name || '…'}” · any rental crossing ${range}`)
	} else {
		const w = whenLines(r)
		parts.push(w.dates === 'Any date' ? 'Any pickup date' : `Pickup ${w.dates}`)
	}
	const chips = whereChips(r, labels).map(c => c.text)
	parts.push(chips.length ? chips.join(' · ') : 'everywhere')
	if (isBinding(r)) parts.push(`floors from ${matrix?.name || 'a matrix'}`)
	else {
		if (r.min_price != null) parts.push(`floor ${fmtMoney(r.min_price)} €/day`)
		if (r.max_price != null) parts.push(`ceiling ${fmtMoney(r.max_price)} €/day`)
		if (r.min_price == null && r.max_price == null) parts.push('no amount yet')
	}
	return parts.join(' · ')
}
